/**
 * IconMultiSelect
 *
 * A thin wrapper around react-select's multi-select that renders an icon or
 * image alongside each option label (both in the dropdown list and in the
 * selected "pill" tags). Used by IconMultiSelectFilterConfig for filters whose
 * options carry a provider logo — e.g. Benchmark, Region and Integration.
 *
 * The component is controlled: it receives `selectedValues` (an array of option
 * values) and reports changes back via `onChange`. It also honours the same
 * mutual-exclusion rules as the plain MultiSelect via `deselectOthers` /
 * `deselectSpecific` on each option (see `updateSelectedValues`).
 */
import React from 'react';
import Select, {
  ClearIndicatorProps,
  components,
  MultiValueGenericProps,
} from 'react-select';

/** A single selectable option. */
export interface Option {
  label: string;
  value: string;
  /** Font-awesome (or similar) class rendered when no `imageSrc` is provided. */
  iconClass?: string;
  /** Image URL (e.g. a provider logo) rendered to the left of the label. */
  imageSrc?: string;
  /** When selected, clears every other selection (used for an "All" option). */
  deselectOthers?: boolean;
  /** When selected, clears these specific values from the current selection. */
  deselectSpecific?: string[];
}

interface IconMultiSelectProps {
  options: Option[];
  /** Currently selected option values (controlled input). */
  selectedValues: string[];
  /** Called with the next array of selected values whenever selection changes. */
  onChange: (values: string[]) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

/** Renders an option's icon/image (if any) followed by its label. */
const renderOptionContent = (option: Option) => {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {option.imageSrc ? (
        <img
          src={option.imageSrc}
          alt={option.label}
          style={{
            width: 20,
            height: 20,
            objectFit: 'contain',
          }}
        />
      ) : option.iconClass ? (
        <i className={option.iconClass} />
      ) : null}

      <span>{option.label}</span>
    </div>
  );
};

/**
 * Custom label for the selected "pill" tags so they show the option's icon/image
 * in addition to the text.
 */
const MultiValueLabel = (props: MultiValueGenericProps<Option>) => {
  const option = props.data as Option;

  return (
    <components.MultiValueLabel {...props}>
      {renderOptionContent(option)}
    </components.MultiValueLabel>
  );
};

/**
 * Keeps the clear-all mousedown inside the select. Clearing synchronously
 * removes the clicked indicator from the DOM; without stopping propagation,
 * SearchAndFilters subsequently sees the detached event target as an outside
 * click and closes the entire filter panel.
 */
const ClearIndicator = (props: ClearIndicatorProps<Option, true>) => {
  const { innerProps } = props;

  return (
    <components.ClearIndicator
      {...props}
      innerProps={{
        ...innerProps,
        onMouseDown: (event) => {
          event.stopPropagation();
          innerProps.onMouseDown?.(event);
        },
      }}
    />
  );
};

const IconMultiSelect: React.FC<IconMultiSelectProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder = 'Select...',
  isDisabled = false,
}) => {
  /**
   * Computes the next selection after the given `option` is toggled, applying
   * that option's mutual-exclusion rules. Mirrors MultiSelect.tsx so both
   * render styles behave identically:
   *  - `deselectOthers`: selecting it clears everything else ("All" behaviour).
   *  - `deselectSpecific`: selecting it removes the listed values.
   *  - otherwise: plain toggle, but any active `deselectOthers` option is
   *    dropped so "All" and a concrete value are never selected together.
   */
  const updateSelectedValues = (
    currentValues: string[],
    option: Option,
  ): string[] => {
    let updatedValues: string[] = [];
    const isSelected = currentValues.includes(option.value);

    if (option.deselectOthers) {
      updatedValues = isSelected ? [] : [option.value];
    } else if (option.deselectSpecific) {
      updatedValues = isSelected
        ? currentValues.filter((v) => v !== option.value)
        : [
            ...currentValues
              .filter((v) => !option.deselectSpecific?.includes(v))
              .filter(
                (v) => !options.find((o) => o.deselectOthers && o.value === v),
              ),
            option.value,
          ];
    } else {
      updatedValues = isSelected
        ? currentValues.filter((v) => v !== option.value)
        : [
            ...currentValues.filter(
              (v) => !options.find((o) => o.deselectOthers && o.value === v),
            ),
            option.value,
          ];
    }

    return updatedValues;
  };

  // Map the controlled `selectedValues` back to the full Option objects that
  // react-select needs as its `value`.
  const selectedOptions = options.filter((option) =>
    selectedValues.includes(option.value),
  );

  const handleChange = (selected: readonly Option[] | null) => {
    // Cleared entirely (e.g. via the "x" clear indicator).
    if (!selected || selected.length === 0) {
      onChange([]);
      return;
    }

    const newSelectedValues = selected.map((item) => item.value);

    // Determine which single option was toggled by diffing react-select's new
    // selection against the previous one, then re-derive the result through
    // updateSelectedValues so the deselect rules are applied consistently.
    const addedOption = options.find(
      (option) =>
        newSelectedValues.includes(option.value) &&
        !selectedValues.includes(option.value),
    );

    const removedOption = options.find(
      (option) =>
        selectedValues.includes(option.value) &&
        !newSelectedValues.includes(option.value),
    );

    const changedOption = addedOption || removedOption;

    if (!changedOption) return;

    const updatedValues = updateSelectedValues(selectedValues, changedOption);

    onChange(updatedValues);
  };

  return (
    <Select
      isMulti
      isDisabled={isDisabled}
      options={options}
      value={selectedOptions}
      onChange={handleChange}
      placeholder={placeholder}
      closeMenuOnSelect={false}
      hideSelectedOptions={false}
      formatOptionLabel={(option) => renderOptionContent(option)}
      components={{ MultiValueLabel, ClearIndicator }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      menuPlacement="auto"
      maxMenuHeight={300}
      styles={{
        control: (base) => ({
          ...base,
          cursor: 'pointer',
        }),
        option: (base) => ({
          ...base,
          cursor: 'pointer',
        }),
        menuPortal: (base) => ({
          ...base,
          zIndex: 9999,
        }),
      }}
    />
  );
};

export default IconMultiSelect;
